import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { supabase } from '../lib/supabase';
import PageLayout from '../components/PageLayout';
import VideoModal from '../components/VideoModal';
import { Helmet } from 'react-helmet-async';

interface Video {
  id: string;
  video_id: string;
  title: string;
  description: string;
}

const LearningCenter = () => {
  const [videos, setVideos] = useState<Video[]>([]);
  const [selectedVideo, setSelectedVideo] = useState<Video | null>(null);

  useEffect(() => {
    const fetchVideos = async () => {
      try {
        const { data, error } = await supabase
          .from('youtube_videos')
          .select('*')
          .order('created_at', { ascending: false });

        if (error) throw error;
        if (data) setVideos(data);
      } catch (error) {
        console.error('Error fetching videos:', error);
      }
    };

    fetchVideos();
  }, []);

  return (
    <PageLayout
      title="Learning Center"
      subtitle="Explore our collection of expert tips and insights about home building"
    >
      <Helmet>
        <title>Home Building Tips & Insights Kerala | Master Home</title>
        <meta name="description" content="Explore Master Home's Learning Center for expert advice, tips, and insights on custom home construction and renovations in Kerala." />
        <link rel="canonical" href="https://masterhome.co.in/learning-center" />
      </Helmet>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
        {videos.map((video, index) => (
          <motion.div
            key={video.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="glass-card overflow-hidden group hover:shadow-xl transition-all duration-300 cursor-pointer"
            onClick={() => setSelectedVideo(video)}
          >
            <div className="relative pt-[56.25%] bg-gray-900">
              <div className="absolute inset-0 bg-gradient-to-t from-primary-600/90 via-primary-500/50 to-transparent opacity-0 group-hover:opacity-100 transition-all duration-300 z-10"></div>
              <img
                src={`https://img.youtube.com/vi/${video.video_id}/maxresdefault.jpg`}
                alt={video.title}
                className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
              />
              <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300 z-20">
                <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center transform group-hover:scale-110 transition-transform duration-300">
                  <svg
                    className="w-8 h-8 text-primary-600 transform translate-x-0.5"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path d="M8 5v14l11-7z" />
                  </svg>
                </div>
              </div>
            </div>
            <div className="p-6 relative">
              <h3 className="text-xl font-bold mb-2 group-hover:text-primary-600 transition-colors">
                {video.title}
              </h3>
              <p className="text-gray-600 line-clamp-2">
                {video.description}
              </p>
            </div>
          </motion.div>
        ))}
      </div>

      <VideoModal
        isOpen={!!selectedVideo}
        onClose={() => setSelectedVideo(null)}
        videoId={selectedVideo?.video_id || ''}
        title={selectedVideo?.title || ''}
      />
    </PageLayout>
  );
};

export default LearningCenter;
